import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';
import {
  CommandPaletteComponent,
  CommandPaletteService,
} from '@presencelearning/ui/command-palette';
import { DrawerComponent } from '@presencelearning/ui/drawer';
import { NavigationComponent, NavigationTriggerDirective } from '@presencelearning/ui/navigation';
import { ThemeToggleComponent } from '@presencelearning/ui/theme';
import { TopbarComponent } from '@presencelearning/ui/topbar';
import { LogoComponent } from './logo/logo.component';

@Component({
  imports: [
    RouterOutlet,
    TopbarComponent,
    NavigationComponent,
    NavigationTriggerDirective,
    DrawerComponent,
    ThemeToggleComponent,
    CommandPaletteComponent,
    MatIconModule,
    LogoComponent,
    TranslocoPipe,
  ],
  selector: 'app-root',
  styleUrl: './app.scss',
  templateUrl: './app.html',
})
export class App {
  protected readonly commandPalette = inject(CommandPaletteService);
  private readonly transloco = inject(TranslocoService);

  // pl-navigation takes `pages` as a plain array of resolved strings, not a
  // template expression the `transloco` pipe can re-render on its own -
  // recompute it manually whenever the active language changes.
  protected readonly activeLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  // No user session yet to persist a real language preference against, so a
  // manual topbar toggle is the only way to reach the non-default language
  // today - detectBrowserLocale only covers first-load, not switching.
  protected readonly nextLang = computed(() => (this.activeLang() === 'en' ? 'es' : 'en'));

  protected readonly navLinks = computed(() => {
    this.activeLang();
    return [
      { label: this.transloco.translate('nav.home'), routerLink: '/', icon: 'home' },
      { label: this.transloco.translate('nav.about'), routerLink: '/about', icon: 'info' },
    ];
  });

  readonly drawerOpen = signal(false);

  toggleLanguage(): void {
    this.transloco.setActiveLang(this.nextLang());
  }

  openSearch(): void {
    this.drawerOpen.set(false);
    this.commandPalette.open();
  }

  openAiChat(): void {
    this.commandPalette.close();
    this.drawerOpen.set(true);
  }
}
